import { type Editor } from '@tiptap/core';
import { useEffect, useRef } from 'react';

import { isSlashDebugEnabled, isSlashReplayEnabled, isSlashRetryEnabled } from './slashCore';
import { createSlashFocusRuntime } from './slashFocusRuntime';

type UseSlashFocusLifecycleParams = {
  editor: Editor | null;
  fieldName: string;
  enableSlashCommands: boolean;
};

export const useSlashFocusLifecycle = ({ editor, fieldName, enableSlashCommands }: UseSlashFocusLifecycleParams) => {
  const editorMountedAtRef = useRef(0);
  const lastSlashFocusAtRef = useRef(0);
  const inFlightPendingFocusTsRef = useRef<number | null>(null);
  const inFlightPendingFocusStartedAtRef = useRef(0);

  useEffect(() => {
    if (!editor) return;

    editorMountedAtRef.current = Date.now();

    const runtime = createSlashFocusRuntime({
      editor,
      fieldName,
      enableSlashCommands,
      debugSlash: isSlashDebugEnabled(),
      replayEnabled: isSlashReplayEnabled(),
      retryEnabled: isSlashRetryEnabled(),
      refs: {
        editorMountedAtRef,
        lastSlashFocusAtRef,
        inFlightPendingFocusTsRef,
        inFlightPendingFocusStartedAtRef,
      },
    });

    window.addEventListener('phx:focusInput', runtime.handleFocusInput as EventListener);
    document.addEventListener('phx:focusInput', runtime.handleFocusInput as EventListener);
    runtime.consumePendingFocus();
    const shouldStartRetryLoop = enableSlashCommands || Boolean(window.__superTextFocusInputMap?.[fieldName]);
    const stopRetryLoop = shouldStartRetryLoop ? runtime.startPendingFocusRetryLoop() : () => undefined;

    return () => {
      window.removeEventListener('phx:focusInput', runtime.handleFocusInput as EventListener);
      document.removeEventListener('phx:focusInput', runtime.handleFocusInput as EventListener);
      stopRetryLoop();
      runtime.destroy();
    };
  }, [editor, fieldName, enableSlashCommands]);
};
